/**
 * MechaGuard - GameState (场景状态机)
 *
 * 场景：菜单 → 游玩 ⇄ 暂停，游玩 → 胜利/失败，任意场景可回到菜单。
 *
 * 注意：`reset()` 把状态复位为 MENU（而不是直接跳到 PLAYING），
 * 由调用方再显式 `set(PLAYING)`。这样主菜单/暂停/结算的切换路径
 * 始终受同一张转换表约束，不会出现「已回到主菜单但状态仍是 PLAYING、
 * 于是按空格弹出暂停层」这类不一致。
 */

export const GameScene = Object.freeze({
  MENU: 'menu',
  PLAYING: 'playing',
  PAUSED: 'paused',
  VICTORY: 'victory',
  DEFEAT: 'defeat',
});

/** 允许的状态转换 */
const VALID_TRANSITIONS = Object.freeze({
  [GameScene.MENU]: [GameScene.MENU, GameScene.PLAYING],
  [GameScene.PLAYING]: [
    GameScene.PAUSED,
    GameScene.VICTORY,
    GameScene.DEFEAT,
    GameScene.MENU,
  ],
  [GameScene.PAUSED]: [GameScene.PLAYING, GameScene.MENU],
  [GameScene.VICTORY]: [GameScene.MENU, GameScene.PLAYING],
  [GameScene.DEFEAT]: [GameScene.MENU, GameScene.PLAYING],
});

export class GameState {
  constructor() {
    this._current = GameScene.MENU;
    /** 最近若干次状态，便于排查切换异常 */
    this.history = [];
  }

  /** @returns {string} 当前场景 */
  get current() {
    return this._current;
  }

  /**
   * 切换场景。非法转换只告警不阻断（保证游戏不会卡死），
   * 但会输出控制台警告方便定位。
   * @param {string} scene
   */
  set(scene) {
    const allowed = VALID_TRANSITIONS[this._current];

    if (scene !== this._current && (!allowed || !allowed.includes(scene))) {
      console.warn(`[GameState] 非法状态转换: ${this._current} -> ${scene}`);
    }

    this._current = scene;
    this.history.push(scene);
    if (this.history.length > 32) this.history.shift();
  }

  /** 复位到主菜单（开新局前调用，随后需再 set(PLAYING)） */
  reset() {
    this._current = GameScene.MENU;
    this.history = [];
  }
}
